import { FC, useCallback, useEffect, useState } from 'react';
import { Alert, Checkbox, FormControlLabel } from '@mui/material';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgFxConversion } from 'services/api/paymentGateway/types';
import { DATE_FILTER } from 'ui/EasTable/constants';
import { EIconButtons } from 'ui/EasTable/EasTableRow/EasTableValue/constants';
import {
	ESortDirection,
	ETableStructureAlign,
	ETableStructureType,
	TTableStructure,
	TTableStructureButton,
} from 'ui/EasTable/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgConfirmDialog } from '../common/PgConfirmDialog';
import { PgList } from '../common/PgList';
import { useReloadKey } from '../common/helpers';
import { pgActionsColumn, pgColumn, statusColumn } from '../common/tableHelpers';
import { usePaymentViewer } from '../Payments/PaymentViewer';
import { formatMoney } from '../utils';

type TRow = TPgFxConversion;
const col = (
	name: string,
	selector: keyof TRow & string,
	type: ETableStructureType,
	extra: Partial<TTableStructure<keyof TRow, TRow>> = {},
) => pgColumn<TRow>(name, selector, type, extra);

const OPEN_PAYMENT = 'Payment';
const SYNC = 'Sync';
const RETRY = 'Retry';
const APPROVE = 'Approve';

const STATUSES = [
	'waiting_funds',
	'requested',
	'scheduled',
	'settled',
	'overdue',
	'cancelled',
	'failed',
	'unknown',
	'not_required',
	'manual_review',
];
/** Retry is possible for these; `overdue` needs a separate confirmation (03 §4.10). */
const RETRY_STATUSES = ['failed', 'cancelled', 'overdue'];

const buttonsFor = (item: TRow): TTableStructureButton[] => {
	const buttons: TTableStructureButton[] = [
		{ name: OPEN_PAYMENT, label: 'Open the payment', icon: EIconButtons.SUMMARIZE },
		{ name: SYNC, label: 'Re-read from Airwallex', icon: EIconButtons.HISTORY },
	];
	if (RETRY_STATUSES.includes(item.status)) {
		buttons.push({ name: RETRY, label: 'Retry the conversion', icon: EIconButtons.UNDO });
	}
	if (item.status === 'manual_review') {
		buttons.push({ name: APPROVE, label: 'Approve', icon: EIconButtons.CHECK_CIRCLE });
	}
	return buttons;
};

const STRUCTURE: TTableStructure<keyof TRow, TRow>[] = [
	col('Created', 'createdAt', ETableStructureType.DATE_TIME, {
		sortable: true,
		filterable: true,
		filter: DATE_FILTER,
	}),
	col('Status', 'status', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		...statusColumn(STATUSES),
	}),
	col('Sell', 'sellAmount', ETableStructureType.STRING, {
		align: ETableStructureAlign.RIGHT,
		value: (item) => formatMoney(item.sellAmount, item.sellCurrency),
	}),
	col('Buy', 'buyAmount', ETableStructureType.STRING, {
		align: ETableStructureAlign.RIGHT,
		value: (item) => (item.buyAmount === null ? '' : formatMoney(item.buyAmount, item.buyCurrency)),
	}),
	col('Rate', 'clientRate', ETableStructureType.STRING, {
		value: (item) => (item.clientRate === null ? '' : String(item.clientRate)),
	}),
	col('Attempt', 'seq', ETableStructureType.STRING, { value: (item) => String(item.seq) }),
	col('Reference', 'shortReference', ETableStructureType.STRING),
	col('Conversion date', 'conversionDate', ETableStructureType.DATE),
	col('Settled', 'settledAt', ETableStructureType.DATE_TIME),
	col('Next check', 'nextCheckAt', ETableStructureType.DATE_TIME),
	col('Error', 'lastError', ETableStructureType.STRING, {
		tooltip: (item) => item.lastError ?? '',
	}),
	pgActionsColumn<TRow>([], buttonsFor),
];

type TDialog = { kind: 'retry' | 'approve'; row: TRow };

/**
 * GBP → EUR conversions of Direct Debit payments (03 §4.10): sync, retry of a failed / cancelled /
 * overdue conversion (an overdue one only after the operator checked that Airwallex did not settle
 * it) and approval of a conversion in manual review.
 */
export const FxConversionsList: FC = () => {
	const { openPayment, changeCounter } = usePaymentViewer();
	const [dialog, setDialog] = useState<TDialog | null>(null);
	const [confirmNotSettled, setConfirmNotSettled] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [reloadKey, reload] = useReloadKey();

	useEffect(() => {
		setConfirmNotSettled(false);
		setError(null);
		setBusy(false);
	}, [dialog]);

	const handleAction = useCallback(
		(row: TRow, action: string) => {
			if (action === OPEN_PAYMENT) {
				openPayment(row.paymentTransactionId);
			} else if (action === SYNC) {
				api.paymentGateway
					.syncFxConversion(row.id)
					.then(() => {
						notificationContainer('Conversion re-read from Airwallex', 'success');
						reload();
					})
					.catch((e) => notificationContainer(getPgErrorMessage(e, 'Sync failed'), 'error'));
			} else if (action === RETRY) {
				setDialog({ kind: 'retry', row });
			} else if (action === APPROVE) {
				setDialog({ kind: 'approve', row });
			}
		},
		[openPayment, reload],
	);

	const submit = async (note: string) => {
		if (!dialog) return;
		setBusy(true);
		setError(null);
		try {
			if (dialog.kind === 'retry') {
				await api.paymentGateway.retryFxConversion({
					id: dialog.row.id,
					body: { confirmNotSettled },
				});
				notificationContainer('A new conversion attempt is created', 'success');
			} else {
				await api.paymentGateway.approveFxConversion({ id: dialog.row.id, body: { note } });
				notificationContainer('Conversion approved', 'success');
			}
			setDialog(null);
			reload();
		} catch (e) {
			setError(getPgErrorMessage(e, 'The action failed'));
		} finally {
			setBusy(false);
		}
	};

	const overdue = dialog?.kind === 'retry' && dialog.row.status === 'overdue';

	return (
		<>
			<PgList<TRow>
				title="FX conversions"
				structure={STRUCTURE}
				fetcher={api.paymentGateway.getFxConversions}
				onAction={handleAction}
				defaultOrder={{ fieldName: 'createdAt', direction: ESortDirection.DESC }}
				reloadKey={reloadKey + changeCounter}
				errorText="Failed to load FX conversions"
			/>
			<PgConfirmDialog
				open={!!dialog}
				title={dialog?.kind === 'approve' ? 'Approve the conversion' : 'Retry the conversion'}
				confirmLabel={dialog?.kind === 'approve' ? 'Approve' : 'Retry'}
				confirmColor="warning"
				reasonLabel={dialog?.kind === 'approve' ? 'Note (shown in the audit log)' : undefined}
				confirmDisabled={overdue && !confirmNotSettled}
				busy={busy}
				error={error}
				onConfirm={(note) => void submit(note)}
				onClose={() => setDialog(null)}
				description={
					dialog?.kind === 'approve'
						? 'A payment moved from the old system is waiting for a check. Confirm that you compared it with the Airwallex web app and the old system did not convert it: the conversion then starts as usual.'
						: 'A new conversion attempt is created with a new request ID. The old attempt stays in the history.'
				}
			>
				{overdue && (
					<>
						<Alert severity="warning" sx={{ mb: 1 }}>
							The conversion is overdue. If Airwallex settled it after all, a second conversion
							would sell the money twice.
						</Alert>
						<FormControlLabel
							control={
								<Checkbox
									checked={confirmNotSettled}
									onChange={(e) => setConfirmNotSettled(e.target.checked)}
									disabled={busy}
								/>
							}
							label="I checked in Airwallex: this conversion is not settled"
						/>
					</>
				)}
			</PgConfirmDialog>
		</>
	);
};
